import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, X } from "lucide-react";
import { sharedApi } from "@/lib/api";
import { useAuth } from "@/lib/auth";

/** Iter 655/658 — Free prize draw card on the public event page.
 *  GET /classifieds/events/{id}/prize (JWT optional) → {sponsorship,
 *  entries_count, my_entry, eligible}. Renders nothing when the event
 *  isn't sponsored. Enter flow: login → verified mobile → terms sheet →
 *  POST …/prize/enter. */

const TYPE_EMOJI = { points: "💰", voucher: "🎟️", gift: "🎁" };

function prizeLine(s) {
  const prefix = s.prize_type === "voucher" ? "Gift voucher: " : s.prize_type === "gift" ? "Gift: " : "";
  return `${prefix}${s.prize_label || ""}`;
}

function drawDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ""
    : d.toLocaleString("en-NZ", { dateStyle: "medium", timeStyle: "short" });
}

export default function PrizeDrawCard({ eventId }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();
  const [termsOpen, setTermsOpen] = useState(false);

  const q = useQuery({
    queryKey: ["event-prize", eventId, user?.id || "guest"],
    queryFn: async () => (await sharedApi.get(`/classifieds/events/${eventId}/prize`)).data,
    retry: 0,
  });

  const enterMut = useMutation({
    mutationFn: async () =>
      (await sharedApi.post(`/classifieds/events/${eventId}/prize/enter`, {})).data,
    onSuccess: () => {
      setTermsOpen(false);
      qc.invalidateQueries({ queryKey: ["event-prize", eventId] });
    },
    onError: (err) => {
      setTermsOpen(false);
      const status = err?.response?.status;
      const needs = err?.response?.headers?.["x-needs"];
      if (status === 401) {
        nav("/login", { state: { from: location.pathname } });
        return;
      }
      if (status === 403 && needs === "phone_verification") {
        toast.error("Verify your mobile number to enter the draw.");
        nav("/account/edit");
        return;
      }
      if (status === 409) {
        toast.success("You're already in this draw.");
        qc.invalidateQueries({ queryKey: ["event-prize", eventId] });
        return;
      }
      if (status === 410) {
        toast.error("This draw has already been done.");
        qc.invalidateQueries({ queryKey: ["event-prize", eventId] });
        return;
      }
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Couldn't enter the draw — please try again.");
    },
  });

  if (q.isLoading || q.isError) return null;
  const s = q.data?.sponsorship;
  if (!s || !s.is_sponsored) return null;

  const entered = !!q.data?.my_entry;
  const won = !!q.data?.my_entry?.won;
  const drawn = s.status === "drawn";
  const eligible = q.data?.eligible; // true | false | null (logged out)

  const onEnterClick = () => {
    if (!user) {
      nav("/login", { state: { from: location.pathname } });
      return;
    }
    if (eligible === false) {
      toast.error("Verify your mobile number to enter the draw.");
      nav("/account/edit");
      return;
    }
    setTermsOpen(true);
  };

  return (
    <div
      data-testid="cevd-prize-card"
      className="rounded-3xl bg-white shadow-card overflow-hidden border border-amber-100"
    >
      <div className="bg-[#FF6200] text-white text-[11px] font-black uppercase tracking-widest px-5 py-2">
        ★ AllSale Sponsored
      </div>
      <div className="p-5">
        <div className="font-display text-lg font-black text-slate-900">
          🎁 Free prize draw
        </div>
        <div className="mt-1 font-bold text-slate-800">
          {TYPE_EMOJI[s.prize_type] || "🎁"} Win {prizeLine(s)}
        </div>
        {s.prize_points > 0 && (
          <div className="text-xs text-slate-500">
            {s.prize_points.toLocaleString("en-NZ")} points (${(s.prize_points / 100).toFixed(2)})
            {s.physical_prize ? ` + ${s.physical_prize}` : ""}
          </div>
        )}
        <div className="mt-2 text-xs text-slate-500">
          Draw on {drawDate(s.draw_at)} · {q.data?.entries_count ?? 0} entered
        </div>

        {drawn ? (
          <div className="mt-4" data-testid="cevd-prize-drawn">
            {won ? (
              <div className="rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm font-bold p-3">
                🎉 You won! Check your wallet and inbox.
              </div>
            ) : (
              <div className="rounded-xl bg-slate-50 border border-soft text-slate-600 text-sm p-3">
                {entered
                  ? "Draw complete — not your lucky day this time."
                  : "This draw has finished."}{" "}
                <span className="text-slate-400">Drawn · winners notified</span>
              </div>
            )}
          </div>
        ) : entered ? (
          <div
            className="mt-4 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm font-bold p-3"
            data-testid="cevd-prize-entered"
          >
            ✓ You&apos;re in! We&apos;ll message you if you win on {drawDate(s.draw_at)}.
          </div>
        ) : (
          <button
            type="button"
            onClick={onEnterClick}
            data-testid="cevd-prize-enter"
            className="mt-4 w-full rounded-full bg-[color:var(--brand-orange)] hover:bg-[color:var(--brand-orange-hover)] text-white font-bold py-2.5 transition-colors"
          >
            Register for a chance to win — FREE
          </button>
        )}

        <button
          type="button"
          onClick={() => setTermsOpen(true)}
          className="mt-3 text-xs font-semibold text-[color:var(--brand-blue)] hover:underline"
        >
          Draw terms
        </button>
      </div>

      {termsOpen && (
        <div
          className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-slate-900/60 backdrop-blur-sm p-3 md:p-6"
          onClick={() => setTermsOpen(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            data-testid="cevd-prize-terms"
            className="w-full max-w-md rounded-2xl bg-white shadow-2xl p-6"
          >
            <div className="flex items-start justify-between gap-3">
              <h3 className="font-display text-lg font-black text-slate-900">
                Prize draw terms
              </h3>
              <button
                type="button"
                onClick={() => setTermsOpen(false)}
                aria-label="Close terms"
                className="w-8 h-8 rounded-full grid place-items-center text-slate-500 hover:bg-slate-100"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <ul className="mt-3 space-y-1.5 text-sm text-slate-600 list-disc pl-5">
              <li>Free entry — no purchase necessary</li>
              <li>One entry per AllSale account (verified mobile)</li>
              <li>
                Draw on {drawDate(s.draw_at)} · {s.winners_count || 1} winner
                {(s.winners_count || 1) > 1 ? "s" : ""}
              </li>
              <li>Prize: {prizeLine(s)}</li>
            </ul>
            {s.terms && (
              <p className="mt-3 text-xs text-slate-500 whitespace-pre-wrap">{s.terms}</p>
            )}
            <div className="mt-5 flex gap-2">
              <button
                type="button"
                onClick={() => setTermsOpen(false)}
                className="flex-1 rounded-full border border-soft text-slate-700 font-bold py-2.5 hover:bg-slate-50 transition-colors"
              >
                Close
              </button>
              {!entered && !drawn && (
                <button
                  type="button"
                  disabled={enterMut.isPending}
                  onClick={() => enterMut.mutate()}
                  data-testid="cevd-prize-confirm"
                  className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-[color:var(--brand-orange)] hover:bg-[color:var(--brand-orange-hover)] text-white font-bold py-2.5 disabled:opacity-70 transition-colors"
                >
                  {enterMut.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
                  I agree — enter me
                </button>
              )}
            </div>
            {!user && (
              <p className="mt-3 text-center text-xs text-slate-500">
                <Link to="/login" className="font-bold text-[color:var(--brand-blue)] hover:underline">
                  Sign in
                </Link>{" "}
                to enter the draw.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
